import React from 'react';

import { useSelector } from 'react-redux';

import PropTypes from 'prop-types';

import dynamic from 'next/dynamic';

import { ErrorBoundary } from '@bonnet/next/error-boundary';

import { useFeatures } from '@atc/features';

import { PageSection } from 'reaxl';

import vdpResultsDuck from '@/ducks/vdp/vdpResultsDuck.js';

import LazyComponent from '@/components/LazyComponent';
import OwnerDetails from '@/components/vehicledetails/OwnerDetails';

import DealerDiffContainer from './DealerDiffContainer';

const SimilarInventoryContainer = dynamic(() => import('./SimilarInventoryContainer'), {
    ssr: true,
    loading: () => (
        <div
            id="placeholder-similar-inv"
            style={{ height: 370 }}
        />
    ),
});

export function OwnerDetailsContainer({
    chatClickHandler,
    disableContactCTAs = false,
    onEmailCTAClick,
    phoneClickHandler,
    textClickHandler,
}) {
    const listing = useSelector(vdpResultsDuck.selectors.getVdpActiveInventory) || {};
    const { isConquestable } = listing;
    const {
        similar_inventory: [isSimilarInventoryEnabled, {
            override_conquestable: overrideConquestableEnabled,
        }],
        vdp_carousel_higher_placement: [isVdpCarouselHigherPlacementEnabled],
        dealer_diff_disabled: [isDealerDiffDisabled],
        enable_new_dealer_card_vdp: [isNewDealerCardVdpEnabled],
    } = useFeatures(['similar_inventory', 'vdp_carousel_higher_placement', 'dealer_diff_disabled', 'enable_new_dealer_card_vdp']);

    const isConquestableFinal = overrideConquestableEnabled ? false : isConquestable;

    // hide owner details only if isConquestable is false AND new dealer card is enabled
    const hideOwnerDetails = !isConquestableFinal && isNewDealerCardVdpEnabled;
    // Dealer config hides some UI components based on dealer preference
    if (listing?.dealerConfig?.hideOwnerDetails) return null;

    return (
        <PageSection
            alignment="left"
            className="container-fluid"
            data-qaid="ownerDetails"
            sectionContainerClassNames={hideOwnerDetails ? '' : 'row margin-top-6'}
        >
            <ErrorBoundary>
                {!hideOwnerDetails
                    && (
                        <OwnerDetails
                            chatClickHandler={chatClickHandler}
                            disableContactCTAs={disableContactCTAs}
                            onEmailCTAClick={onEmailCTAClick}
                            phoneClickHandler={phoneClickHandler}
                            textClickHandler={textClickHandler}
                        />
                    )}
            </ErrorBoundary>
            {(isSimilarInventoryEnabled && !isVdpCarouselHigherPlacementEnabled) && (
                <LazyComponent
                    key="owner_similar_inventory"
                    fallback={(
                        <div
                            id="placeholder-similar-inv"
                            style={{ height: 370 }}
                        />
                    )}
                >
                    <div>
                        <SimilarInventoryContainer />
                    </div>
                </LazyComponent>
            )}
            {(!isDealerDiffDisabled) && (
                <DealerDiffContainer />
            )}
        </PageSection>
    );
}

OwnerDetailsContainer.propTypes = {
    /**
     * Boolean to disable phone/chat/email for the Hyundai experience
     */
    disableContactCTAs: PropTypes.bool,
    /**
     * The callback function fires when email CTA is clicked
     */
    onEmailCTAClick: PropTypes.func,
    /**
     * The callback function fires when chat CTA is clicked
     */
    chatClickHandler: PropTypes.func,
    /**
     * The callback function fires when owner phone is called
     */
    phoneClickHandler: PropTypes.func,
    /**
     * The callback function fires when text CTA is clicked
     */
    textClickHandler: PropTypes.func,
};

export default OwnerDetailsContainer;
